/* eslint-disable max-lines */
import { useAuth0 } from '@auth0/auth0-react';
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ChangeRequestSubType, RequestType, RequestTypeDisplayTextMap } from '@strata/react-chat';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { Hyperlink } from '@strata/react-hyperlink';
import { TextAreaFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast, WarningToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { MemberEscalation } from 'components/requests/dialogs/MemberEscalation';
import { PayerRequestWritePermissions } from 'services/constants';
import { ReadPermissionRequestTypeMap } from 'services/payerRequests';
import { PBMOnlyOptions } from 'services/pbmOnlyOptions';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetPayerUserWithPermNameMapQuery } from 'store/api/authApi';
import { type RequestDetails, usePostNewRequestMutation } from 'store/api/payerRequestApi';
import { useGetSegmentsQuery } from 'store/api/referenceApi';
import { type TreatmentDetails } from 'store/api/treatmentApi';

const UNASSIGNED = 'Unassigned';

const hideAdditionalInfoSection = ({
    requestType,
    requestSubType,
}: Pick<RequestDetails, 'requestType' | 'requestSubType' | 'segmentId'>) =>
    !requestType || (requestType === RequestType.CHANGE && !requestSubType);

const isProductChangeRequest = ({
    requestType,
    requestSubType,
}: Pick<RequestDetails, 'requestType' | 'requestSubType'>) =>
    requestType === RequestType.CHANGE && requestSubType === ChangeRequestSubType.PRODUCT;

const ChangeRequestSubTypeDisplayTextMap = {
    [ChangeRequestSubType.PHARMACY]: 'pharmacy',
    [ChangeRequestSubType.PRODUCT]: 'product',
} as const;

const useDefaultSegmentOption = (segmentOptions?: { name: string; value: string }[]) => {
    const { segmentId } = useAppSelector((state) => state.views);

    // If segment is selected in views, use that segment
    if (segmentId) {
        return { segmentId };
    }

    // If there is only one segment option, select it
    if (segmentOptions?.length === 1) {
        return { segmentId: segmentOptions[0].value };
    }

    // Otherwise, force user to select segment
    return undefined;
};

interface SubmitRequestDialogProps {
    isOpen: boolean;
    onClose: () => void;
    treatmentDetails?: TreatmentDetails;
}

export const SubmitRequestDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
}: SubmitRequestDialogProps) => {
    const { user } = useAuth0();
    const navigate = useNavigate();
    const [postNewRequest] = usePostNewRequestMutation();
    const { payerId, permissions, segmentIds, restrictedAccess } = useAppSelector(
        (state) => state.auth,
    );

    const currentUserHasPBMOnlyAccess = permissions.includes(
        PayerRequestWritePermissions.WRITE_PAYER_REQUEST_PBM_ONLY,
    );
    const [segmentId, setSegmentId] = useState<string>();
    const [requestTypeState, setRequestTypeState] = useState<string>();

    const { data: allUsers, isFetching: usersWithPermFetching } =
        useGetPayerUserWithPermNameMapQuery(
            segmentId && requestTypeState
                ? {
                      segmentId,
                      permission: `write:payer_request_${requestTypeState.toLowerCase()}`,
                      includePBMOnlyAccess: currentUserHasPBMOnlyAccess,
                  }
                : skipToken,
        );

    const payerAssigneeOptions = [
        { value: UNASSIGNED, name: UNASSIGNED, hasPBMOnlyAccess: true },
        { value: user?.email, name: 'Assign to Me', hasPBMOnlyAccess: true },
        ...Object.entries({ ...allUsers })
            .filter(([upn]) => upn !== user?.email)
            .filter(([upn]) => !upn.endsWith('@freemarkethealth.com'))
            .map(([upn, { full_name: fullName, has_pbm_only_access: hasPBMOnlyAccess }]) => ({
                value: upn,
                name: fullName,
                hasPBMOnlyAccess,
            })),
    ];

    const { data: segments, isFetching: isFetchingSegments } = useGetSegmentsQuery({
        payerId: payerId!,
    });
    const segmentOptions = (segments?.segmentsList ?? [])
        .filter(({ id }) => segmentIds.includes(id))
        .map((segment) => ({ name: segment.name, value: segment.id }));

    const defaultSegmentOption = useDefaultSegmentOption(segmentOptions);

    const requestTypeOptions = Object.entries(ReadPermissionRequestTypeMap)
        .filter(([permission]) => permissions.includes(permission))
        .map(([_, value]) => ({
            value,
            name: RequestTypeDisplayTextMap[value],
        }));

    /** True if opened from the treatment details screen */
    const openedFromTreatment = !!treatmentDetails;

    const validationSchema = Yup.object({
        segmentId: Yup.string().required(validationStrings.requiredField.errorMessage),
        ...MemberEscalation.validationSchema({ restrictedAccess }),
        requestSubType: Yup.string().when('requestType', {
            is: (requestType: RequestDetails['requestType']) =>
                (
                    [RequestType.ESCALATION, RequestType.CHANGE] as RequestDetails['requestType'][]
                ).includes(requestType),
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        additionalNotes: Yup.string().when(['requestType', 'requestSubType', 'typeLevel2'], {
            is: (
                requestType: RequestDetails['requestType'],
                requestSubType: RequestDetails['requestSubType'],
                typeLevel2: RequestDetails['typeLevel2'],
            ) =>
                requestType === RequestType.OTHER ||
                (requestType === RequestType.CHANGE &&
                    requestSubType !== ChangeRequestSubType.PRODUCT) ||
                (requestType === RequestType.ESCALATION &&
                    requestSubType === 'Resolve Claim Rejection' &&
                    typeLevel2 === 'Other'),
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        requiredFile: Yup.array().when(['requestType', 'requestSubType'], {
            is: (
                requestType: RequestDetails['requestType'],
                requestSubType: RequestDetails['requestSubType'],
            ) => isProductChangeRequest({ requestType, requestSubType }),
            then: (schema) =>
                schema
                    .required(validationStrings.requiredField.errorMessage)
                    .length(1, validationStrings.requiredField.errorMessage),
        }),
        requiredFileUpload: Yup.array(),
        additionalFiles: Yup.array(),
        additionalFilesUpload: Yup.array(),
        urgent: Yup.bool(),
        pbmOnly: Yup.string(),
        payerAssignedUpn: Yup.string().when(['pbmOnly'], {
            is: (pbmOnly: string) => pbmOnly === 'true',
            then: (schema) =>
                schema.notOneOf(
                    payerAssigneeOptions
                        .filter((option) => !option.hasPBMOnlyAccess)
                        .map((option) => option.value),
                    'Not a PBM user.',
                ),
        }),
    });

    return (
        <Formik
            initialValues={
                (openedFromTreatment
                    ? {
                          segmentId: treatmentDetails.segmentId,
                          requestType: RequestType.ESCALATION,
                          pbmOnly: currentUserHasPBMOnlyAccess ? 'true' : 'false',
                          ...MemberEscalation.initialValues(treatmentDetails),
                      }
                    : {
                          pbmOnly: currentUserHasPBMOnlyAccess ? 'true' : 'false',
                          ...defaultSegmentOption,
                      }) as Yup.InferType<typeof validationSchema>
            }
            enableReinitialize
            validateOnMount
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
                const response = await postNewRequest({
                    payerId: payerId!,
                    segmentId: values.segmentId,
                    requestType: values.requestType,
                    requestSubType: values.requestSubType,
                    typeLevel2: values.typeLevel2,
                    memberId: values.memberId,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    dateOfBirth: formatDate(values.dob, DateFormats.HYPHEN_DATE),
                    treatmentIds: values.selectedTreatments ?? [],
                    additionalNotes: values.additionalNotes,
                    files: [
                        ...((values.requiredFileUpload ?? []) as File[]),
                        ...((values.additionalFilesUpload ?? []) as File[]),
                    ],
                    urgent: values.urgent ?? false,
                    pbmOnly: values.pbmOnly === 'true',
                    payerAssignedUpn: values.payerAssignedUpn,
                }).unwrap();
                resetForm();
                onClose();
                if (response.unverifiedMember) {
                    WarningToast({
                        message: 'Request created successfully with an unverified member.',
                    });
                } else {
                    SuccessToast({ message: 'Request created successfully.' });
                }
                navigate(AppRoutes.requestDetails(response.requestId));
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Submit Request"
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Submit Request"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            {!openedFromTreatment && (
                                <>
                                    {segmentIds.length !== 1 && (
                                        <SuggestSingleSelectFormik
                                            id="segmentId"
                                            label="Business Segment"
                                            required
                                            disableAutoPopulate
                                            isLoading={isFetchingSegments}
                                            values={segmentOptions}
                                            selectedItem={segmentOptions.find(
                                                ({ value }) => value === formik.values.segmentId,
                                            )}
                                            onChange={(newVal) => {
                                                void formik.setValues({
                                                    segmentId: newVal?.value,
                                                    pbmOnly: formik.values.pbmOnly,
                                                } as Yup.InferType<typeof validationSchema>);
                                                if (newVal) {
                                                    setSegmentId(newVal.value);
                                                }
                                            }}
                                        />
                                    )}
                                    <SuggestSingleSelectFormik
                                        id="requestType"
                                        label="What Can We Help You With?"
                                        required
                                        values={requestTypeOptions}
                                        selectedItem={requestTypeOptions.find(
                                            ({ value }) => value === formik.values.requestType,
                                        )}
                                        onChange={(newVal) => {
                                            void formik.setValues({
                                                segmentId: formik.values.segmentId,
                                                requestType: newVal?.value,
                                                pbmOnly: formik.values.pbmOnly,
                                            } as Yup.InferType<typeof validationSchema>);
                                            if (newVal) {
                                                setRequestTypeState(newVal.value);
                                            }
                                        }}
                                    />
                                </>
                            )}
                            <MemberEscalation openedFromTreatment={openedFromTreatment} />
                            {formik.values.requestType === RequestType.CHANGE && (
                                <>
                                    <SuggestSingleSelectFormik
                                        id="requestSubType"
                                        label="Please Select One Of The Following"
                                        values={Object.values(ChangeRequestSubType)}
                                        required
                                        onChange={(newVal) => {
                                            void formik.setValues({
                                                segmentId: formik.values.segmentId,
                                                requestType: formik.values.requestType,
                                                requestSubType: newVal,
                                                pbmOnly: formik.values.pbmOnly,
                                            });
                                        }}
                                    />
                                    {currentUserHasPBMOnlyAccess && (
                                        <RadioButtonGroupFormik
                                            name="pbmOnly"
                                            label="Request Visibility"
                                            options={PBMOnlyOptions}
                                            required
                                        />
                                    )}
                                    {(() => {
                                        if (!formik.values.requestSubType) {
                                            return null;
                                        }

                                        return isProductChangeRequest(formik.values) ? (
                                            <>
                                                <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                                                    {`${
                                                        ChangeRequestSubTypeDisplayTextMap[
                                                            formik.values
                                                                .requestSubType as keyof typeof ChangeRequestSubTypeDisplayTextMap
                                                        ]
                                                    } Information`}
                                                </div>
                                                <FileUploadFormik
                                                    id="requiredFile"
                                                    label="Upload Product File"
                                                    required
                                                    accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                                                    maxAllowedFiles={1}
                                                    onAddFiles={(files) =>
                                                        formik.setFieldValue(
                                                            'requiredFileUpload',
                                                            files,
                                                        )
                                                    }
                                                    onRemoveFile={() =>
                                                        formik.setFieldValue(
                                                            'requiredFileUpload',
                                                            undefined,
                                                        )
                                                    }
                                                    helperText={
                                                        <div
                                                            className="flex__gap--small"
                                                            style={{ flexDirection: 'column' }}
                                                        >
                                                            <div>
                                                                Please use the template file below
                                                                to upload product information.
                                                            </div>
                                                            <Hyperlink
                                                                navUrl="https://sseasacdn.blob.core.windows.net/platform-assets/Product-Management-Template.xlsx"
                                                                linkText={
                                                                    <div className="flex__gap--small">
                                                                        <span>
                                                                            Product-Management-Template.xlsx
                                                                        </span>
                                                                        <Icon
                                                                            icon={
                                                                                IconNames.DOWNLOAD
                                                                            }
                                                                        />
                                                                    </div>
                                                                }
                                                            />
                                                        </div>
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <TextAreaFormik
                                                id="additionalNotes"
                                                label="Please Describe The Change Required"
                                                required
                                            />
                                        );
                                    })()}
                                </>
                            )}
                            {formik.values.requestType === RequestType.OTHER &&
                                currentUserHasPBMOnlyAccess && (
                                    <RadioButtonGroupFormik
                                        name="pbmOnly"
                                        label="Request Visibility"
                                        options={PBMOnlyOptions}
                                        required
                                    />
                                )}
                            {formik.values.requestType === RequestType.OTHER && (
                                <TextAreaFormik
                                    id="additionalNotes"
                                    label="Please Describe The Request"
                                    required
                                />
                            )}
                            {!hideAdditionalInfoSection(formik.values) &&
                                !usersWithPermFetching && (
                                    <>
                                        <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                                            Assignment
                                        </div>
                                        <SuggestSingleSelectFormik
                                            id="payerAssignedUpn"
                                            label="Payer Assignee"
                                            isLoading={usersWithPermFetching}
                                            values={payerAssigneeOptions.filter((option) =>
                                                formik.values.pbmOnly === 'true'
                                                    ? option?.hasPBMOnlyAccess
                                                    : true,
                                            )}
                                            selectedItem={payerAssigneeOptions.find(
                                                ({ value }) =>
                                                    value === formik.values.payerAssignedUpn,
                                            )}
                                            onChange={(newVal) => {
                                                void formik.setFieldValue(
                                                    'payerAssignedUpn',
                                                    newVal?.value,
                                                );
                                            }}
                                        />
                                        <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                                            Additional Information
                                        </div>
                                        {(formik.values.requestType === RequestType.ESCALATION ||
                                            isProductChangeRequest(formik.values)) && (
                                            <TextAreaFormik
                                                id="additionalNotes"
                                                label="Anything Else You Would Like Us To Know?"
                                                required={
                                                    formik.values.requestSubType ===
                                                        'Resolve Claim Rejection' &&
                                                    formik.values.typeLevel2 === 'Other'
                                                }
                                            />
                                        )}
                                        <FileUploadFormik
                                            id="additionalFiles"
                                            label="Attach File(s)"
                                            accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,application/pdf"
                                            helperText="Accepted file types: xlsx, docx, csv, and pdf"
                                            onAddFiles={(files) =>
                                                formik.setFieldValue('additionalFilesUpload', [
                                                    ...((formik.values.additionalFilesUpload ??
                                                        []) as File[]),
                                                    ...files,
                                                ])
                                            }
                                            onRemoveFile={(filename) =>
                                                formik.setFieldValue(
                                                    'additionalFilesUpload',
                                                    formik.values.additionalFilesUpload?.filter(
                                                        (file: File) => file.name !== filename,
                                                    ),
                                                )
                                            }
                                        />
                                        <CheckboxListFormik
                                            name="urgent"
                                            label="Request Urgency"
                                            options={['Urgent']}
                                            values={formik.values.urgent ? ['Urgent'] : []}
                                            onChange={(newValue) => {
                                                void formik.setFieldValue(
                                                    'urgent',
                                                    newValue.includes('Urgent'),
                                                );
                                            }}
                                        />
                                    </>
                                )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
